import React, { useState, useMemo } from "react";
import { FaTrophy, FaUsers, FaCrown } from "react-icons/fa";
import { BsShieldFill } from "react-icons/bs";
import { MdGroups } from "react-icons/md";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import AnimateHeight from "react-animate-height";
import { useCockpitPreferences } from "@/hooks/play/useCockpitPreferences";
import { useAsset } from "@/hooks/useAsset";
import useGetTopGuildsLeaderboard from "@/hooks/useGetTopGuildsLeaderboard";
import useGetTopGuildLeadersLeaderboard from "@/hooks/useGetTopGuildLeadersLeaderboard";
import useGetGlobalMonthlyGuildLeaderboard from "@/hooks/useGetGlobalMonthlyGuildLeaderboard";
import { getDateInEst } from "@/utils/getDateInEst";
import goldMedalUrl from "@/svgs/gofrens-gold-medal-icon.svg";
import silverMedalUrl from "@/svgs/gofrens-silver-medal-icon.svg";
import bronzeMedalUrl from "@/svgs/gofrens-bronze-medal-icon.svg";
import "./GuildsLeaderboardCard.scss";

enum LeaderboardView {
    GUILDS = "guilds",
    GUILD_LEADERS = "guild_leaders",
    GLOBAL_LEADERS = "global_leaders",
}

interface GuildsLeaderboardCardProps {
    fullToken?: any;
    // Guild info from me-column integration
    userId?: string;
    guildId?: string;
    guild?: any;
    guildProgress?: any;
    guildMembersData?: any;
    membersAroundUser?: any;
    onViewAll?: () => void;
    fullView?: boolean;
}

const GuildsLeaderboardCard: React.FC<GuildsLeaderboardCardProps> = ({
    fullToken,
    userId,
    guildId,
    guild,
    guildProgress,
    guildMembersData,
    membersAroundUser,
    onViewAll,
    fullView = false,
}) => {
    const [currentView, setCurrentView] = useState<LeaderboardView>(LeaderboardView.GUILDS);
    const { isExpanded, toggleCard } = useCockpitPreferences();
    const expanded = isExpanded("guilds-leaderboard", true);

    // Find user's position and contribution within guild (from MyGuildCard logic)
    const userGuildStats = useMemo(() => {
        if (!userId) return { rank: null, contribution: 0 };

        // First check membersAroundUser (like /guilds page does)
        if (membersAroundUser && userId) {
            const rankedUser = membersAroundUser.find((member: any) => member.id === userId);
            if (rankedUser) {
                return {
                    rank: rankedUser.rank,
                    contribution: rankedUser.totalPoints || 0,
                };
            }
        }

        // Fallback to guildMembersData.members array
        if (guildMembersData?.members && userId) {
            const memberIndex = guildMembersData.members.findIndex((m: any) => m.userId === userId || m.id === userId);
            if (memberIndex >= 0) {
                const member = guildMembersData.members[memberIndex];
                return {
                    rank: member.rank || memberIndex + 1,
                    contribution: member.totalPoints || member.xp || 0,
                };
            }
        }

        return { rank: null, contribution: 0 };
    }, [membersAroundUser, guildMembersData, userId]);

    // Use guild from props or fallback to guildProgress
    const guildData = guild || guildProgress?.guild;
    const guildRank = guildData?.monthlyRank;

    // Get guild icon/avatar using useAsset hook
    const { data: guildIcon } = useAsset(guildData?.icon || "");

    // Calculate date param
    const dateParam = useMemo(() => {
        const now = getDateInEst();
        return `${now.getMonth() + 1},${now.getFullYear()}`;
    }, []);

    // Fetch data
    const { data: guildsData } = useGetTopGuildsLeaderboard(
        `${dateParam},0`,
        currentView === LeaderboardView.GUILDS,
    );

    const { data: leadersData } = useGetTopGuildLeadersLeaderboard(
        `${dateParam},0`,
        currentView === LeaderboardView.GUILD_LEADERS,
    );

    const { data: globalData } = useGetGlobalMonthlyGuildLeaderboard(
        `${dateParam},0`,
        currentView === LeaderboardView.GLOBAL_LEADERS,
    );

    // Get the appropriate data based on current view
    const leaderboardData = useMemo(() => {
        switch (currentView) {
            case LeaderboardView.GUILDS:
                return guildsData?.guilds?.slice(0, 5) || [];
            case LeaderboardView.GUILD_LEADERS:
                return leadersData?.leaders?.slice(0, 5) || [];
            case LeaderboardView.GLOBAL_LEADERS:
                return globalData?.members?.slice(0, 5) || [];
            default:
                return [];
        }
    }, [currentView, guildsData, leadersData, globalData]);

    const renderGuildRow = (guild: any, index: number) => {
        const rank = index + 1;
        return (
            <div key={`guild-${guild.id || index}`} className="guilds-leaderboard-card__row">
                <div className="rank">
                    {rank === 1
                        ? <img src={goldMedalUrl} alt="Gold Medal" className="medal" />
                        : rank === 2
                            ? <img src={silverMedalUrl} alt="Silver Medal" className="medal" />
                            : rank === 3
                                ? <img src={bronzeMedalUrl} alt="Bronze Medal" className="medal" />
                                : <span className="rank-number">{rank}</span>
                    }
                </div>
                <div className="avatar">
                    {guild.icon
                        ? <img src={guild.icon} alt={guild.name} />
                        : <FaUsers />
                    }
                </div>
                <div className="info">
                    <div className="name">{guild.name}</div>
                    <div className="stats">
                        <span className="xp">{(guild.monthlyPoints || guild.totalPoints || 0).toLocaleString()} XP</span>
                    </div>
                </div>
            </div>
        );
    };

    const handleToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
        e.preventDefault();
        if (e.type === "click" || (e as React.KeyboardEvent).key === "Enter" || (e as React.KeyboardEvent).key === " ") {
            toggleCard("guilds-leaderboard");
        }
    };

    const renderLeaderRow = (leader: any, index: number) => {
        const rank = index + 1;
        return (
            <div key={`leader-${leader.id || index}`} className="guilds-leaderboard-card__row guilds-leaderboard-card__row--individual">
                <div className="rank">
                    {rank === 1
                        ? <img src={goldMedalUrl} alt="Gold Medal" className="medal" />
                        : rank === 2
                            ? <img src={silverMedalUrl} alt="Silver Medal" className="medal" />
                            : rank === 3
                                ? <img src={bronzeMedalUrl} alt="Bronze Medal" className="medal" />
                                : <span className="rank-number">{rank}</span>
                    }
                </div>
                <div className="avatar">
                    {leader.profilePicture
                        ? <img src={leader.profilePicture} alt={leader.nickname} />
                        : <div className="avatar-placeholder">
                            {(leader.nickname || "P")[0].toUpperCase()}
                        </div>
                    }
                </div>
                <div className="info">
                    <div className="name">
                        {leader.nickname || "Player"}
                        <span className="xp">{(leader.totalPoints || 0).toLocaleString()} XP</span>
                    </div>
                    {leader.guildName &&
            <div className="guild-name">{leader.guildName}</div>
                    }
                </div>
            </div>
        );
    };

    return (
        <div className={`guilds-leaderboard-card ${expanded ? "expanded" : "collapsed"}`}>
            <div
                className="guilds-leaderboard-card__header"
                onClick={handleToggle}
                onKeyDown={handleToggle}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-controls="guilds-leaderboard-content"
            >
                <h4>
                    <BsShieldFill className="header-icon" />
                    Guilds
                </h4>
                <button
                    className="guilds-leaderboard-card__collapse-toggle"
                    aria-label={expanded ? "Collapse" : "Expand"}
                >
                    {expanded ? <FiChevronUp /> : <FiChevronDown />}
                </button>
            </div>

            <AnimateHeight
                id="guilds-leaderboard-content"
                duration={300}
                height={expanded ? "auto" : 0}
            >
                <div className="guilds-leaderboard-card__content-wrapper">

                    {/* My Guild Section - integrated from MyGuildCard */}
                    {guildId && guildData
                        ? <div className="guilds-leaderboard-card__my-guild">
                            <div className="my-guild-header">
                                <div className="guild-identity">
                                    <div className="guild-avatar">
                                        {guildIcon?.url
                                            ? <img src={guildIcon.url} alt={guildData.name} />
                                            : <MdGroups />
                                        }
                                    </div>
                                    <div className="guild-info">
                                        <h5 className="guild-name">
                                            {guildRank && guildRank > 0 && <span className="rank-badge">#{guildRank}</span>}
                                            {guildData.name || "My Guild"}
                                        </h5>
                                        <div className="guild-member-count">
                                            {guildData.memberCount || 0} Members
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="my-guild-stats">
                                <div className="stat-row stat-row--rank">
                                    <span className="label">My Rank</span>
                                    <span className="value">{userGuildStats.rank ? `#${userGuildStats.rank}` : "Unranked"}</span>
                                </div>
                                <div className="stat-row">
                                    <span className="label">My XP</span>
                                    <span className="value">
                                        {userGuildStats.contribution.toLocaleString()} <span className="xp-suffix">XP</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        : !guildId
                            ? <div className="guilds-leaderboard-card__no-guild">
                                <div className="no-guild-icon">🛡️</div>
                                <div className="no-guild-content">
                                    <h4>Join a Guild</h4>
                                    <p>Squad up and compete!</p>
                                    <button
                                        className="join-guild-btn"
                                        onClick={() => window.location.href = "/guilds"}
                                    >
                                        Browse Guilds
                                    </button>
                                </div>
                            </div>
                            : null}

                    <div className="guilds-leaderboard-card__toggle">
                        <button
                            className={currentView === LeaderboardView.GUILDS ? "active" : ""}
                            onClick={() => setCurrentView(LeaderboardView.GUILDS)}
                        >
                            Guilds
                        </button>
                        <button
                            className={currentView === LeaderboardView.GUILD_LEADERS ? "active" : ""}
                            onClick={() => setCurrentView(LeaderboardView.GUILD_LEADERS)}
                        >
                            Leaders
                        </button>
                        <button
                            className={currentView === LeaderboardView.GLOBAL_LEADERS ? "active" : ""}
                            onClick={() => setCurrentView(LeaderboardView.GLOBAL_LEADERS)}
                        >
                            Global
                        </button>
                    </div>

                    <div className="guilds-leaderboard-card__content">
                        {leaderboardData.length === 0
                            ? <div className="empty-state">
                                <p>No data available</p>
                            </div>
                            : <div className={`guilds-leaderboard-card__list ${currentView !== LeaderboardView.GUILDS ? "guilds-leaderboard-card__list--individual" : ""}`}>
                                {currentView === LeaderboardView.GUILDS
                                    ? leaderboardData.map(renderGuildRow)
                                    : leaderboardData.map(renderLeaderRow)
                                }
                            </div>
                        }
                    </div>

                    <div className="guilds-leaderboard-card__footer">
                        <button
                            className="view-all-btn"
                            onClick={onViewAll}
                        >
                            View Full Leaderboard
                        </button>
                    </div>
                </div>
            </AnimateHeight>
        </div>
    );
};

export default React.memo(GuildsLeaderboardCard);